import { IReportFilters } from './reports';
import API from '../api/api';
import { ApiRoutes } from '../constans/api';
import { filterEmptyFilters, stringifyArraysInObject } from '../helpers/helpers';
export interface ISurveyItem {
  id: string;
  organization: string;
  name: string;
  status: string;
  endDate: string;
  type: string;
}

export interface IPagingInfo {
  pageNo: number | null;
  itemsPerPage: number | null;
  totalPageCount: number | null;
  totalItemsCount: number | null;
  memberCount: number | null;
  subordinateCount: number | null;
  superordinateCount: number | null;
  leaderCount: number | null;
  teamReportsCount: number | null;
  executiveReportsCount: number | null;
}

export interface ISurveyResp {
  items: ISurveyItem[];
  pagingInfo: IPagingInfo;
}

export interface ISurveyFilters {
  organizationId?: string | null;
  organizationName?: string | null;
  status?: string | null;
  sortOrder?: string | null;
  ln?: string | null;
  pageNo?: string | null | undefined;
  perPage?: string | null | undefined;
  [x: string]: string | null | undefined;
}

/** Backend: ReportingGroupsQueryParamsSerializer — unknown keys raise 400. */
const REPORTING_GROUPS_LIST_QUERY_KEYS = [
  'perPage',
  'pageNo',
  'type',
  'search',
  'sortOrder',
] as const;

/** Backend: ParticipantsQueryParamsSerializer */
const PARTICIPANTS_LIST_QUERY_KEYS = ['perPage', 'pageNo', 'language', 'search'] as const;

/** Backend: ReportingGroupMembersQueryParamsSerializer */
const REPORTING_GROUP_MEMBERS_QUERY_KEYS = ['perPage', 'pageNo', 'search'] as const;

function pickAllowedSurveyQueryParams(
  filters: ISurveyFilters,
  allowedKeys: readonly string[]
): ISurveyFilters {
  return allowedKeys.reduce<ISurveyFilters>((acc, key) => {
    const value = filters[key];
    if (value != null && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function pickReportingGroupMembersQueryParams(filters: IReportFilters): ISurveyFilters {
  return REPORTING_GROUP_MEMBERS_QUERY_KEYS.reduce<ISurveyFilters>((acc, key) => {
    const value = filters[key as keyof IReportFilters];
    if (value != null && value !== '') {
      acc[key] = typeof value === 'string' ? value : String(value);
    }
    return acc;
  }, {});
}

export const getSurveysList = async (params: ISurveyFilters): Promise<ISurveyResp> => {
  const { surveyList } = ApiRoutes;
  const { data } = await API.get(surveyList, {
    params,
  });
  return data;
};

export interface ICreateSurvey {
  organizationId: string;
  type: string;
  name: string;
  baseLanguage?: string;
}

interface ICreateSurveyResp {
  id: string;
}

export const createSurveyRequest = async (
  surveyData: ICreateSurvey
): Promise<ICreateSurveyResp> => {
  const { surveyList } = ApiRoutes;
  const { data } = await API.post(surveyList, surveyData);

  return data;
};
export interface IGetSurveyOverviewResp {
  id: string;
  organizationName: string;
  type: string;
  name: string;
  baseLanguage: string;
  externalSurveyId: string;
}

export const getSurveyOverviewRequest = async (
  surveyId: string
): Promise<IGetSurveyOverviewResp> => {
  const { surveyList } = ApiRoutes;
  const { data } = await API.get(`${surveyList}${surveyId}/`);
  return data;
};

export interface ISurveyContentsItem {
  code: string;
  estimatedResponseTime: number;
}

export interface ISurveyContentsCategory {
  code: string;
  topics: {
    code: string;
    items: ISurveyContentsItem[];
  }[];
}

export interface IGetSurveyContentsRequest {
  scope: ISurveyContentsCategory[];
  helpText: Record<string, string> | null;
  introText: Record<string, string> | null;
  outroText: Record<string, string> | null;
  languages: string[];
  openQuestions: { code: string }[];
  title: Record<string, string> | null;
  preview_link: string;
}

export const getSurveyContentsRequest = async (
  surveyId: string
): Promise<IGetSurveyContentsRequest> => {
  const { surveyList, contents } = ApiRoutes;
  const { data } = await API.get(`${surveyList}${surveyId}${contents}`);
  return data;
};

export interface ISurveyScopeTopic {
  code: string;
  items: string[];
}

export interface ISurveyScope {
  code: string;
  topics: ISurveyScopeTopic[];
}

export interface IIntroOutro {
  introText: { en?: string; nb?: string };
  outroText: { en?: string; nb?: string };
  title: { en?: string; nb?: string };
  helpText: { en?: string; nb?: string };
}
export interface IPutSurveyContentPayload {
  scope: ISurveyScope[];
  openQuestions: { code: string }[];
}

export const putSurveyContentsRequest = async (
  surveyId: string,
  payload: IPutSurveyContentPayload
) => {
  const { surveyList, contents } = ApiRoutes;
  const { data } = await API.put(`${surveyList}${surveyId}${contents}`, payload);
  return data;
};

export interface ITeamMember {
  id: string;
  name: string;
  emailAddress: string;
  phoneNo: string;
  supervisorName: string;
  preferredLanguage: string;
}

export interface IParticipantsItem {
  id: string;
  name: string;
  emailAddress: string;
  phoneNo?: string;
  preferredLanguage: string;
  team?: string;
  leader?: string;
  role?: string;
  department?: string;
  status: { code: string; datetime: string };
}

export interface IDepartmentItem {
  id: string;
  memberCount: {
    director: string;
    employee: string;
    manager: string;
  };
  name: string;
  type: string;
}

export interface IParticipantsRes {
  items: IParticipantsItem[];
  pagingInfo: IPagingInfo;
}

export interface IDepartmentsRes {
  items: IDepartmentItem[];
  pagingInfo: IPagingInfo;
}

export const getParticipantsRequest = async (
  surveyId: string,
  filters: ISurveyFilters
): Promise<IParticipantsRes> => {
  const params = pickAllowedSurveyQueryParams(filters, PARTICIPANTS_LIST_QUERY_KEYS);
  const { surveyList, participants } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${participants}`;
  const { data } = await API.get(url, { params });
  return data;
};

export const getDepartmentsRequest = async (
  surveyId: string,
  params?: { pageNo?: number; perPage?: number }
): Promise<IDepartmentsRes> => {
  const { surveyList, reportingGroups } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${reportingGroups}`;
  const { data } = await API.get(url, { params: params || { pageNo: 1, perPage: 10 } });
  return data;
};

export interface IPostParticipantsPayload {
  name: string;
  preferredLanguage: string;
}

export const patchParticipantsTeamRequest = async (
  payload: IPostParticipantsPayload,
  surveyId: string,
  teamId: string
) => {
  const { surveyList, participantsTeams } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${participantsTeams}${teamId}/`;
  const { data } = await API.patch(url, payload);
  return data;
};

export const deleteParticipant = async (surveyId: string, participantId: string) => {
  const { surveyList, participants } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${participants}${participantId}`;
  const { data } = await API.delete(url);
  return data;
};

export interface IPostTeamMemberBody {
  name: string;
  emailAddress: string;
  phoneNo: string;
  supervisorName: string;
  preferredLanguage: string;
}

export const postAddMemberToParticipantsTeam = async (
  body: IPostTeamMemberBody,
  surveyId: string,
  teamId: string
) => {
  try {
    const { surveyList, participantsTeams, members } = ApiRoutes;
    const url = `${surveyList}${surveyId}/${participantsTeams}${teamId}/${members}`;
    const { data } = await API.post(url, body);
    return data;
  } catch ({ data }) {
    const { details } = data;
    throw stringifyArraysInObject(details);
  }
};

export const patchParticipantTeamMember = async (
  body: IPostTeamMemberBody,
  surveyId: string,
  teamId: string,
  participantId: string
) => {
  try {
    const { surveyList, participantsTeams, members } = ApiRoutes;
    const url = `${surveyList}${surveyId}/${participantsTeams}${teamId}/${members}${participantId}/`;
    const { data } = await API.patch(url, body);
    return data;
  } catch ({ data }) {
    const { details } = data;
    throw stringifyArraysInObject(details);
  }
};

export interface IPostParticipantBody {
  name: string;
  emailAddress: string;
  phoneNo: string;
  preferredLanguage: string;
  team?: string;
  leader?: string;
  role?: string;
}

export const postParticipant = async (surveyId: string, body: IPostParticipantBody) => {
  const { surveyList, participants } = ApiRoutes;
  const params = filterEmptyFilters(body as unknown as ISurveyFilters);
  const url = `${surveyList}${surveyId}/${participants}`;
  const { data } = await API.post(url, params);
  return data;
};

export const patchParticipant = async (
  surveyId: string,
  body: IPostParticipantBody,
  participantId: string
) => {
  const { surveyList, participants } = ApiRoutes;
  const params = filterEmptyFilters(body as unknown as ISurveyFilters);
  const url = `${surveyList}${surveyId}/${participants}${participantId}`;
  const { data } = await API.patch(url, params);
  return data;
};

export interface IReportsSetupMemberCount {
  participants?: number;
  groups?: number;
  director?: number;
  employee?: number;
  manager?: number;
  subordinate?: number;
  member?: number;
  superordinate?: number;
  leader?: number;
}

export interface IReportsSetupItem {
  id: string;
  name: string;
  type: 'team' | 'executive';
  members?: IReportsSetupRes;
  memberCount: IReportsSetupMemberCount;
  mappedDepartment?: { id?: string; name?: string };
}

interface IReportsSetupRes {
  items: IReportsSetupItem[];
  pagingInfo: IPagingInfo;
}

export const getReportsSetupRequest = async (
  surveyId: string,
  filters: ISurveyFilters
): Promise<IReportsSetupRes> => {
  const params = pickAllowedSurveyQueryParams(filters, REPORTING_GROUPS_LIST_QUERY_KEYS);
  const { surveyList, reportingGroups } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${reportingGroups}`;
  const { data } = await API.get(url, { params });
  return data;
};

export interface IReportsSetupMember {
  id: string;
  name: string;
  emailAddress: string;
  team?: string;
  leader?: string;
  memberCount?: number;
  role?: string;
}

export interface IReportSetupParticipants {
  items: IReportsSetupMember[];
  pagingInfo: IPagingInfo;
}

export const getReportsSetupParticipants = async (
  surveyId: string,
  groupId: string,
  filters: IReportFilters
): Promise<IReportSetupParticipants> => {
  const { surveyList, reportingGroups, members } = ApiRoutes;
  const params = pickReportingGroupMembersQueryParams(filters);
  const url = `${surveyList}${surveyId}/${reportingGroups}${groupId}/${members}`;
  const { data } = await API.get(url, { params: params });
  return data;
};

export type IPostAddParticipantBody = { participantId: string } | { groupId: string };

export const postAddParticipant = async (
  body: IPostAddParticipantBody,
  surveyId: string,
  groupId: string
) => {
  const { surveyList, reportingGroups, members } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${reportingGroups}${groupId}/${members}`;
  const { data } = await API.post(url, body);
  return data;
};

export interface IPotentialMembersFilters {
  name?: string;
  pageNo?: number;
  perPage?: number;
}

export type IPotentialParticipantsRes = {
  emailAddress: string;
  id: string;
  name: string;
};
export const getReportsPotentialParticipants = async (
  surveyId: string,
  groupId: string,
  filters: IPotentialMembersFilters
) => {
  const { surveyList, reportingGroups, potentialMembers } = ApiRoutes;
  const { name } = filters;
  const params = { name: name?.trim() };
  const transformedParams = params?.name ? params : {};
  const url = `${surveyList}${surveyId}/${reportingGroups}${groupId}/${potentialMembers}`;
  const { data } = await API.get(url, { params: transformedParams });
  return data;
};

export interface IScheduleReminder {
  id: number;
  execute_at: string | null;
  include_sms: boolean;
}

export interface IShareUser {
  id: number;
  name: string;
  email: string;
}

export interface IScheduleSurvey {
  start_date: string | null;
  end_date: string;
  contact_name: string;
  contact_email: string;
  include_sms_invitation: boolean;
  reminders: IScheduleReminder[];
}

export interface IAccessList {
  access_list: number[];
  apply_for_child: boolean;
}

export const getScheduleSurveyRequest = async (surveyId: string): Promise<IScheduleSurvey> => {
  const { surveyList, schedule } = ApiRoutes;
  const url = `${surveyList}${surveyId}/${schedule}`;
  const { data } = await API.get(url);
  return data;
};

export const putScheduleSurveyRequest = async (surveyId: string, payload: IScheduleSurvey) => {
  const { surveyList, schedule } = ApiRoutes;
  const { data } = await API.put(`${surveyList}${surveyId}/${schedule}`, payload);
  return data;
};

export const getSurveyShareUsers = async (surveyId: string): Promise<IShareUser[]> => {
  const { surveyList } = ApiRoutes;
  const { data } = await API.get(`${surveyList}${surveyId}/share/users/`);
  return data;
};

export const putSurveyShareUsers = async (
  projectId: string,
  departmentId: number,
  payload: IAccessList
): Promise<{ access_list: number[]; apply_for_child: boolean }> => {
  const { surveyList } = ApiRoutes;
  const { data } = await API.put(`${surveyList}${projectId}/share/${departmentId}/`, payload);
  return data;
};

export const postShareResults = async (projectId: string) => {
  const { surveyList } = ApiRoutes;
  const { data } = await API.post(`${surveyList}${projectId}/share/send/`);
  return data;
};
